import { CloseOutlined, HolderOutlined } from "@ant-design/icons"
import { Button, Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { homeColor } from "../../home-theme.ts"

/**
 * Edit mode's two per-widget controls.
 *
 * They live INSIDE the widget's own header beside its title, not in a floating
 * bar above the card. The bar was introduced so Remove would not sit on top of
 * the card's Refresh / Expand / Export icons; the header's own flex row solves
 * that without costing every card a strip of empty space above it.
 *
 * Both are real buttons with accessible names, because a drag handle that only
 * answers a pointer leaves a keyboard user with no way to reorder at all.
 */

const CONTROL_STYLE: React.CSSProperties = {
  width: 24,
  height: 24,
  minWidth: 24,
  color: homeColor.inkMuted,
  flexShrink: 0
}

interface DragHandleProps {
  /** The widget's own name, so the control announces what it moves. */
  title?: string
  /** dnd-kit's `attributes` and `listeners`, spread onto the button. */
  dragProps?: Record<string, unknown>
  /**
   * Arrow-key move, for the keyboard. react-grid-layout has no keyboard
   * story of its own, so a handle on the grid wires this instead of
   * dnd-kit's sensors.
   */
  onMove?: (direction: "left" | "right" | "up" | "down") => void
}

/** The class `react-grid-layout` is told to treat as the drag grip. */
export const GRID_HANDLE_CLASS = "home-grid-handle"

export const WidgetDragHandle = ({
  title,
  dragProps,
  onMove
}: DragHandleProps) => {
  const { t } = useTranslation()
  const label = title
    ? t("portfolio.editLayout.reorderWidget", "Reorder {{title}}", { title })
    : t("portfolio.editLayout.reorderWidgetUntitled", "Reorder widget")

  return (
    <Tooltip
      title={t(
        "portfolio.editLayout.dragHint",
        "Drag, or use the arrow keys, to move"
      )}
    >
      <Button
        type="text"
        size="small"
        aria-label={label}
        data-testid="widget-drag-handle"
        className={GRID_HANDLE_CLASS}
        icon={<HolderOutlined />}
        style={{ ...CONTROL_STYLE, cursor: "grab" }}
        onKeyDown={(event) => {
          if (!onMove) return
          const step = {
            ArrowLeft: "left",
            ArrowRight: "right",
            ArrowUp: "up",
            ArrowDown: "down"
          }[event.key] as "left" | "right" | "up" | "down" | undefined
          if (!step) return
          // The grid scrolls on arrows otherwise, and the widget never moves.
          event.preventDefault()
          event.stopPropagation()
          onMove(step)
        }}
        {...dragProps}
      />
    </Tooltip>
  )
}

interface RemoveProps {
  title?: string
  onRemove: () => void
}

export const WidgetRemoveButton = ({ title, onRemove }: RemoveProps) => {
  const { t } = useTranslation()
  const label = title
    ? t("portfolio.editLayout.removeWidgetNamed", "Remove {{title}} widget", {
        title
      })
    : t("portfolio.editLayout.removeWidget", "Remove widget")

  return (
    <Tooltip title={t("portfolio.editLayout.removeWidget", "Remove widget")}>
      <Button
        type="text"
        size="small"
        aria-label={label}
        data-testid="widget-remove"
        icon={<CloseOutlined />}
        style={CONTROL_STYLE}
        // A metric cell is itself a drag handle, so the press that opens this
        // control must not also start a drag.
        onPointerDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        onClick={onRemove}
      />
    </Tooltip>
  )
}
